import clsx from 'clsx'
import React, { useCallback, useMemo, useState } from 'react'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import MobileNavTrigger from 'shared-components/layout/MobileNavTrigger'
import Linked from 'shared-components/service/Linked'
import Percent from 'shared-svg/Percent.svg'
import DesktopNavPlaceholder from 'shared-components/layout/DesktopNavPlaceholder'
import HeaderAuth from 'shared-components/ui/HeaderAuth'
import { AuthContext } from 'shared-components/contexts/AuthContext'
import { AccountAvatar } from 'shared-components/account/AccountAvatar'
import styles from './Header.module.css'
import Logo from 'shared-svg/LogoAA.svg'
import { ScrollUpContext } from 'shared-components/contexts/ScrollUpContext'
import TopSearch from 'shared-components/layout/TopSearch'
import TopSearchControl from 'shared-components/layout/TopSearchControl'
import { publicConfig } from 'config'
import RWButton from 'shared-components/buttons/RWButton'
import { accountHref, accountNotificationLinks } from 'shared-components/account/routes'
import type { AuthMenuItem } from 'shared-components/ui/AuthMenu'
import { PhoneIcon } from 'shared-components/ui/MenuIcons'
import { useMyDevices } from 'lib/devices/MyDevicesContext'
import { knownWithoutPhone } from 'lib/devices/state'

const Header: React.VFC = () => {
  const [activated, setActivated] = useState(false)
  const activatedCb = useCallback(() => setActivated(true), [])
  const { scrolledUp } = ScrollUpContext.useContainer()
  const {
    common: { dealsButton, homeLink, dealsLink, profileLink },
    showAdminPanel,
  } = CommonDataContext.useContainer()
  const searchEnabled = publicConfig('searchEnabled')
  // The tray's "Settings" and "See all" open the account Notifications page (plan D1/D7).
  const notificationLinks = useMemo(
    () => accountNotificationLinks(profileLink.href),
    [profileLink.href]
  )
  // Optional: header chrome renders on every route (Constraint a5808347b).
  const user = AuthContext.useOptionalContainer()?.authState?.user
  // "Your device": an "Add your phone" row while the reader is KNOWN to have no current phone
  // (devices read ready) — never on a loading or failed read. Absent otherwise, so the bare
  // HeaderAuth render stays as pinned (Directive 8cf53b964).
  const devices = useMyDevices()
  const askForPhone = Boolean(user) && knownWithoutPhone(devices)
  const menuItems = useMemo<AuthMenuItem[] | undefined>(() => {
    if (!askForPhone) {
      return undefined
    }
    return [
      {
        id: 'device',
        label: 'Add your phone',
        href: accountHref(profileLink.href, 'device'),
        icon: PhoneIcon,
        meta: 'New',
      },
    ]
  }, [askForPhone, profileLink.href])

  return (
    <header
      role="button"
      tabIndex={0}
      onClick={activatedCb}
      onKeyDown={activatedCb}
      className={clsx(styles.header, {
        [styles.headerActivated]: activated,
        [styles.preview]: showAdminPanel,
        [styles.scrolledUp]: scrolledUp,
      })}
    >
      {searchEnabled && (
        <TopSearch
          className={styles.topSearch}
          control={TopSearchControl}
          resultsClassName={styles.searchResults}
        />
      )}
      <DesktopNavPlaceholder />
      <MobileNavTrigger searchEnabled={searchEnabled} />
      <Linked {...homeLink} className={styles.logoLink}>
        <Logo className={styles.logoIcon} />
      </Linked>
      <RWButton
        variant="tertiary"
        size="medium"
        pLink={dealsLink}
        className={styles.dealsButton}
        ariaAttrs={{
          'aria-label': 'Home',
        }}
      >
        {dealsButton.label}
        <Percent className={styles.dealsIcon} />
      </RWButton>
      <HeaderAuth
        className={styles.authBox}
        notificationLinks={notificationLinks}
        menuItems={menuItems}
        avatar={
          user ? (
            <AccountAvatar
              tone="accent"
              src={user.avatarUrl ?? null}
              name={user.name?.trim() || user.email}
            />
          ) : undefined
        }
      />
    </header>
  )
}

export default Header
